// The stored `runners` row as the domain reads it.
// Split out of `../runner.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { AccountIdSchema, TaskIdSchema } from '../../value-objects/ids';
import { RunnerCapabilitiesSchema } from './capabilities';
import { RunnerMaxSlotsSchema } from './max-slots';
import { RunnerRoleSchema } from './role';
import { BoxSpecsSchema } from './specs';
import {
  RunnerKindSchema,
  RunnerOwnerKindSchema,
  RunnerOwnerTypeSchema,
  RunnerStatusSchema,
} from './status';

export const RunnerSchema = z.object({
  /** Raw uuid PK — no brand (the id is a DB handle, not a domain value object). */
  id: z.string().uuid(),
  accountId: AccountIdSchema,
  status: RunnerStatusSchema,
  /**
   * The box's PINNED role (`runners.mode`, #368) — what work it can be given and
   * whether it converses (see {@link runnerRoleCanConverse}).
   * On the entity, not just on the assigner's narrow `AssignableRunner` row, because
   * it decides ANSWERS A USER SEES: `plan_start` refuses `no_scout_capacity` on it,
   * and an operator told to fix that has to be able to see which of their boxes is
   * in which mode. The column is `NOT NULL DEFAULT 'worker'`, so every row has one.
   */
  mode: RunnerRoleSchema,
  /**
   * The box's jobs-at-once cap (`runners.max_slots`, #6740; epic #6552 v2 ruling
   * V14): how many claims the assigner may hand this box concurrently. The
   * column is `NOT NULL DEFAULT 4` by ruling — this is NEVER null and no reader
   * coalesces, because there is no unset state: "reset" IS "set to
   * {@link RUNNER_MAX_SLOTS_DEFAULT}". The bounds ({@link RunnerMaxSlotsSchema},
   * 1..8) live in domain because the db CHECK, the REST body and the MCP variant
   * all derive from them. Like {@link mode}, the cap governs the CLAIM — what
   * the box may be given — not the run.
   */
  maxSlots: RunnerMaxSlotsSchema,
  /** byovm (owner's box, #110) | fleet (mothership-owned pool, #115). */
  kind: RunnerKindSchema,
  /** personal (bound to `accountId`) | shared (mothership pool, fleet only). */
  ownerType: RunnerOwnerTypeSchema,
  /**
   * Task-claim ownership (the runner-ownership model): `org` (private to one
   * account) | `user` (private to one person, across their orgs). The assigner
   * scopes what this box may claim by this + {@link ownerId}. Defaults to `org`
   * (the tenant-isolation behavior every enrolled box has had).
   */
  ownerKind: RunnerOwnerKindSchema,
  /**
   * The owner reference for {@link ownerKind}: an `accounts.id` (`org`) or a
   * `users.id` (`user`). A null on an `org` box is read as "scoped to its
   * `accountId`" (a legacy row that predates explicit ownership).
   */
  ownerId: z.string().nullable(),
  /**
   * The box's operator-authored system prompt (#455) — a CONCISE briefing on
   * what this VPS runs (e.g. "claude -p on an Anthropic Max sub; prefer it for
   * long-context work") so the orchestrating agent dispatches accordingly.
   * Capped at write ({@link RunnerSystemPromptSchema}); joined into the fleet
   * prompt composition as its own layer for work executing on this box. Null =
   * no briefing (the common plain box).
   */
  systemPrompt: z.string().nullable(),
  hostname: z.string().nullable(),
  runnerVersion: z.string().nullable(),
  /**
   * The box's friendly name — user-set or, by default, deterministically
   * generated from its id ({@link generateDisplayName}). Always a string: a row
   * with no stored name resolves to the generated one at read, so consumers
   * (dashboards, PR/issue comments, notifications) never see a bare UUID.
   */
  displayName: z.string(),
  /**
   * Public address the box last dialed in from — its source IP as seen on the
   * heartbeat (the box dials out, so that hop is the box's public IP). Lets the
   * cockpit discover how to reach the box over SSH. Null until a heartbeat
   * carries one; never set by enrollment (#115 fleet discovery).
   */
  address: z.string().nullable(),
  capabilities: RunnerCapabilitiesSchema,
  /**
   * Static hardware the box advertises ({@link BoxSpecs}) — cpu, cores, ram, disk,
   * arch. Null until a box advertises (legacy rows never did). The assigner may
   * weigh it for placement; live load rides presence ({@link BoxStats}), not here.
   *
   * Written at enroll and re-synced from the presence beat by the reaper
   * (`updateRunnerSpecs`, #3765); see {@link BoxSpecs} for both writers and when
   * the column still goes stale.
   */
  specs: BoxSpecsSchema.nullable(),
  /**
   * Reference to the NATS user creds this runner dials the fleet plane with (a
   * vault key / secret name resolved via `@developerz/config`), never the creds
   * themselves — same hash-only posture as the token columns. Null for BYOVM.
   */
  natsCredsRef: z.string().nullable(),
  /**
   * When the credential this box CURRENTLY HOLDS was issued (#2543) — the JWT
   * `iat` claim `apps/runner/src/creds-refresh.ts` parsed off its on-disk
   * `.creds`. PRIMARY signal the alert lane keys on
   * (`box_creds_expiring` / `box_creds_lapsed` / `box_creds_unknown` in
   * `apps/worker/src/cron/fleet-alerts.ts`): what the box ACTUALLY ADOPTED,
   * never what the mothership minted. A held-`iat` that does not advance across
   * consecutive re-issue requests is unambiguous non-adoption, immune to the
   * 16h healthy cadence and to reboot-forced requests. Null until the box next
   * rotates its credential, then stamped every successful refresh. Alert-gate
   * contract: `box_creds_unknown` raises when this is null on an ONLINE box
   * (adoption itself unknown); the expiry column is checked against this one.
   */
  natsCredsHeldIssuedAt: z.date().nullable(),
  /**
   * When the held credential will lapse (#2543) — the JWT `exp` claim, stamped
   * against the SAME `iat` `mintRunnerCreds` is given so the two agree to the
   * millisecond. Persisted in the SAME statement that rotates `nats_creds_ref`
   * — a second UPDATE could fail independently and leave `nats_creds_ref`
   * rotated against a stale expiry (worse than null). Alert-gate keying:
   * `box_creds_expiring` / `box_creds_lapsed` only raise when this column AND
   * `nats_creds_held_issued_at` are non-null, so the timestamps always travel as
   * a pair.
   */
  natsCredsExpiresAt: z.date().nullable(),
  /**
   * The task this runner is executing right now (`tasks.id`); null when idle.
   *
   * DERIVED by the store from `tasks` (`currentTaskIdSql` in `@developerz/db`),
   * not read off the runner row. There was a `runners.current_task_id` column
   * and nothing ever wrote it — the claim sets `tasks.runner_id` and stops — so
   * this field was null on every box in the live fleet, and the two surfaces
   * that publish it (MCP `fleet_status`, the dashboard box panel) described an
   * eternally idle fleet. Dropped in migration 0142. Anything that wants to
   * know whether a box is busy must ask `tasks`, which is the only party that
   * knows.
   */
  currentTaskId: TaskIdSchema.nullable(),
  lastHeartbeatAt: z.date().nullable(),
  enrolledAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
  /**
   * The operator drain mark (`runners.drained_at`, #6455): when a drain HELD this box
   * out of the assignable pool, null when it is not held. `reviveRunner` refuses a
   * stamped row, so a drained box stays out heartbeat or not until `undrainRunner`
   * clears it. Kept on a revoked row as the record. Published as `drained_iso` so a
   * drained box does not read as one more offline box.
   */
  drainedAt: z.date().nullable(),
  /**
   * The operator CORDON (`runners.cordoned_at`): the graceful drain. Non-null = the
   * assigner hands this box no new work while it finishes what it holds; status,
   * claims and `drainedAt` are untouched. `undrainRunner` clears it. Published as
   * `cordoned_iso`.
   */
  cordonedAt: z.date().nullable(),
  createdAt: z.date(),
});
export type Runner = z.infer<typeof RunnerSchema>;
